import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    CheckCircle, CreditCard, Sparkle, FilePdf, ArrowRight, Key,
    Clock, XCircle, ArrowsClockwise,
} from "@phosphor-icons/react";
import { fmtYmd, fmtYmdTime } from "@/lib/dateFormat";

const PLAN_ORDER = ["starter", "business", "enterprise"];

const PLAN_FEATURES = {
    starter: ["最大 50 台まで登録可能", "使用前点検・写真証跡", "PDF 帳票出力", "メールサポート"],
    business: ["最大 300 台", "AI 写真異常検知", "AI 買替レコメンド", "ドライバー評価", "API連携・Webhook", "優先サポート"],
    enterprise: ["無制限（台数制限なし）", "SSO / IP制限（将来）", "専任カスタマーサクセス", "SLA 99.9%", "カスタム契約"],
};

export default function Billing() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const [plans, setPlans] = useState({});
    const [trialDays, setTrialDays] = useState(14);
    const [current, setCurrent] = useState({});
    const [invoices, setInvoices] = useState([]);
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [polling, setPolling] = useState(false);
    const [newToken, setNewToken] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [a, b, c, d] = await Promise.all([
                api.get("/billing/plans"),
                api.get("/billing/current"),
                api.get("/billing/invoices"),
                api.get("/tokens"),
            ]);
            setPlans(a.data.plans);
            setTrialDays(a.data.trial_days);
            setCurrent(b.data);
            setInvoices(c.data.invoices);
            setTokens(d.data.tokens);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Handle return from Stripe
    useEffect(() => {
        const sid = params.get("session_id");
        const cancelled = params.get("cancelled");
        if (cancelled) {
            toast.info("決済がキャンセルされました");
            const p = new URLSearchParams(params);
            p.delete("cancelled");
            setParams(p, { replace: true });
            return;
        }
        if (!sid || polling) return;
        setPolling(true);
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(`/billing/checkout-status/${sid}`);
                if (data.status === "complete") {
                    toast.success(`プラン「${data.plan}」にアップグレードしました！`);
                    const p = new URLSearchParams(params);
                    p.delete("session_id");
                    setParams(p, { replace: true });
                    setPolling(false);
                    refresh();
                    return;
                }
                if (data.status === "expired") {
                    toast.error("決済セッションが期限切れです。もう一度お試しください。");
                    setPolling(false);
                    return;
                }
                if (attempts < 8) setTimeout(poll, 2500);
                else {
                    toast.info("処理に時間がかかっています。しばらくしてページを再読み込みしてください。");
                    setPolling(false);
                }
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
                setPolling(false);
            }
        };
        poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const subscribe = async (planKey) => {
        try {
            const { data } = await api.post("/billing/checkout", {
                plan: planKey,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const startTrial = async () => {
        try {
            await api.post("/billing/trial/start");
            toast.success(`${trialDays}日間の無料トライアルを開始しました`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const downloadInvoice = async (inv) => {
        try {
            const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
        } catch (e) {
            toast.error("PDFの取得に失敗しました");
        }
    };

    const issueToken = async () => {
        const name = window.prompt("APIトークンの識別名（例：基幹システム連携）");
        if (!name) return;
        try {
            const { data } = await api.post("/tokens", { name });
            setNewToken(data);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const revokeToken = async (id) => {
        if (!window.confirm("このトークンを失効させますか？使用中のシステムは動作しなくなります。")) return;
        try {
            await api.delete(`/tokens/${id}`);
            toast.success("失効しました");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    if (loading) return <div className="p-8 text-[var(--prc-muted)]">読み込み中…</div>;

    const curPlan = current.plan || "starter";
    const subStatus = current.subscription_status || "free";
    const trialUsed = !!current.trial_used;

    return (
        <div className="space-y-10" data-testid="billing-page">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <p className="prc-label-overline">// BILLING · SUBSCRIPTION</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight text-[var(--prc-ink-2)] mt-1">
                        プラン・お支払い
                    </h1>
                    <p className="mt-2 text-sm text-[var(--prc-muted)]">
                        現在のプラン、請求書、API連携トークンを一元管理します。
                    </p>
                </div>
                <button onClick={refresh} className="prc-btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="billing-refresh">
                    <ArrowsClockwise size={16} /> 更新
                </button>
            </div>

            {/* Current subscription summary */}
            <div className="prc-card p-6" data-testid="billing-summary">
                <div className="grid md:grid-cols-4 gap-6">
                    <div>
                        <p className="prc-label-overline">現在のプラン</p>
                        <p className="mt-2 text-2xl font-mono font-bold text-[var(--prc-green)] uppercase">{curPlan}</p>
                    </div>
                    <div>
                        <p className="prc-label-overline">ステータス</p>
                        <p className="mt-2 font-mono font-bold text-[var(--prc-ink-2)]">
                            {subStatus === "active" && <span className="text-[var(--prc-green)]">● アクティブ</span>}
                            {subStatus === "trialing" && <span className="text-[var(--prc-amber)]">● トライアル中</span>}
                            {subStatus === "cancelled" && <span className="text-[var(--prc-red)]">● キャンセル済み</span>}
                            {subStatus === "free" && <span className="text-[var(--prc-muted)]">● 無料</span>}
                        </p>
                    </div>
                    <div>
                        <p className="prc-label-overline">車両上限</p>
                        <p className="mt-2 font-mono text-[var(--prc-ink-2)]">
                            {current.vehicle_limit === 0 ? "無制限" : `${current.vehicle_limit} 台`}
                        </p>
                    </div>
                    <div>
                        <p className="prc-label-overline">次回更新</p>
                        <p className="mt-2 font-mono text-[var(--prc-ink-2)] text-sm">
                            {current.trial_end ? `トライアル: ${fmtYmd(current.trial_end, String(current.trial_end).substring(0, 10))}` :
                             current.current_period_end ? fmtYmd(current.current_period_end, String(current.current_period_end).substring(0, 10)) : "—"}
                        </p>
                    </div>
                </div>
                {!trialUsed && subStatus !== "active" && (
                    <div className="mt-6 p-4 border border-[var(--prc-green)] bg-[rgba(0,255,156,0.05)] rounded-sm flex items-center justify-between" data-testid="trial-banner">
                        <div className="flex items-center gap-3">
                            <Sparkle size={24} className="text-[var(--prc-green)]" weight="fill" />
                            <div>
                                <p className="font-semibold text-[var(--prc-ink-2)]">{trialDays}日間の無料トライアル</p>
                                <p className="text-sm text-[var(--prc-muted)]">Business プランを全機能 {trialDays} 日間、クレカ不要でお試しいただけます。</p>
                            </div>
                        </div>
                        <button className="prc-btn-primary" onClick={startTrial} data-testid="start-trial-btn">
                            無料で開始 <ArrowRight size={16} />
                        </button>
                    </div>
                )}
            </div>

            {/* Plan selection */}
            <section>
                <p className="prc-label-overline mb-3">// SELECT PLAN</p>
                <div className="grid md:grid-cols-3 gap-4" data-testid="plans-grid">
                    {PLAN_ORDER.map((key) => {
                        const p = plans[key];
                        if (!p) return null;
                        const isCurrent = key === curPlan;
                        return (
                            <div key={key} className={`prc-card p-6 flex flex-col ${key === "business" ? "ring-1 ring-[var(--prc-green)]" : ""}`} data-testid={`plan-${key}`}>
                                {key === "business" && <span className="absolute -top-2 left-6 prc-badge ok">人気</span>}
                                <p className="prc-label-overline">{p.label}</p>
                                <p className="mt-2 font-mono text-4xl font-bold tracking-tight text-[var(--prc-ink-2)]">
                                    ¥{p.amount.toLocaleString()}
                                    <span className="text-sm font-normal text-[var(--prc-muted)] ml-2 font-sans">/ 月</span>
                                </p>
                                <ul className="mt-5 space-y-2 text-sm text-[var(--prc-muted)] flex-1">
                                    {PLAN_FEATURES[key].map((f, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <CheckCircle size={16} weight="fill" className="text-[var(--prc-green)] shrink-0 mt-0.5" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                {isCurrent ? (
                                    <button className="prc-btn-ghost mt-6 w-full cursor-default opacity-70" disabled data-testid={`plan-${key}-current`}>
                                        <CheckCircle size={16} weight="fill" /> 現在のプラン
                                    </button>
                                ) : (
                                    <button onClick={() => subscribe(key)} className="prc-btn-primary mt-6 w-full inline-flex items-center justify-center gap-2" data-testid={`plan-${key}-subscribe`}>
                                        <CreditCard size={16} />アップグレード
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Invoice history */}
            <section>
                <p className="prc-label-overline mb-3">// INVOICE HISTORY</p>
                {invoices.length === 0 ? (
                    <div className="prc-card p-8 text-center text-sm text-[var(--prc-muted)]" data-testid="invoices-empty">
                        まだ請求書はありません。プランにアップグレードすると自動的に発行されます。
                    </div>
                ) : (
                    <div className="prc-card overflow-hidden">
                        <table className="prc-table">
                            <thead>
                                <tr>
                                    <th>請求番号</th><th>発行日</th><th>プラン</th>
                                    <th className="num">小計</th><th className="num">消費税</th><th className="num">合計</th>
                                    <th>状態</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.id} data-testid={`invoice-${inv.id}`}>
                                        <td className="text-[var(--prc-accent)]">{inv.invoice_number}</td>
                                        <td>{fmtYmd(inv.issued_at, String(inv.issued_at).substring(0, 10))}</td>
                                        <td>{inv.plan}</td>
                                        <td className="num">¥{(inv.amount || 0).toLocaleString()}</td>
                                        <td className="num">¥{(inv.tax_amount || 0).toLocaleString()}</td>
                                        <td className="num font-bold">¥{(inv.total_amount || 0).toLocaleString()}</td>
                                        <td>
                                            {inv.status === "paid" ? <span className="prc-badge ok">支払済</span> : <span className="prc-badge ng">未払い</span>}
                                        </td>
                                        <td className="text-right">
                                            <button onClick={() => downloadInvoice(inv)} className="prc-btn-ghost text-xs inline-flex items-center gap-1" data-testid={`invoice-pdf-${inv.id}`}>
                                                <FilePdf size={16} /> PDF
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* API Tokens */}
            {(user?.role === "company_admin" || user?.role === "super_admin") && (
                <section>
                    <div className="flex justify-between items-center mb-3">
                        <p className="prc-label-overline">// API TOKENS</p>
                        <button onClick={issueToken} className="prc-btn-accent text-xs inline-flex items-center gap-1.5" data-testid="issue-token-btn">
                            <Key size={16} />新規発行
                        </button>
                    </div>

                    {newToken && (
                        <div className="prc-card p-5 mb-4 border-[var(--prc-amber)]" data-testid="new-token-alert">
                            <p className="prc-label-overline !text-[var(--prc-amber)]">// ONE-TIME DISPLAY</p>
                            <p className="mt-2 text-sm text-[var(--prc-ink-2)]">「{newToken.name}」のトークンを発行しました。<strong className="text-[var(--prc-amber)]">この画面を閉じると二度と表示されません。</strong>安全な場所に保存してください。</p>
                            <div className="mt-3 p-3 bg-[var(--prc-bg)] border border-[var(--prc-border-strong)] rounded-sm font-mono text-xs break-all select-all" data-testid="new-token-value">
                                {newToken.token}
                            </div>
                            <button onClick={() => { navigator.clipboard.writeText(newToken.token); toast.success("コピーしました"); }} className="prc-btn-ghost text-xs mt-3">クリップボードにコピー</button>
                            <button onClick={() => setNewToken(null)} className="prc-btn-ghost text-xs mt-3 ml-2">保存したので閉じる</button>
                        </div>
                    )}

                    {tokens.length === 0 ? (
                        <div className="prc-card p-8 text-center text-sm text-[var(--prc-muted)]" data-testid="tokens-empty">
                            APIトークンがまだ発行されていません。外部システムとの連携時に使用します。
                        </div>
                    ) : (
                        <div className="prc-card overflow-hidden">
                            <table className="prc-table">
                                <thead>
                                    <tr><th>名前</th><th>プレフィックス</th><th>発行者</th><th>作成日</th><th>最終使用</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {tokens.map((t) => (
                                        <tr key={t.id} data-testid={`token-${t.id}`}>
                                            <td>{t.name}</td>
                                            <td className="text-[var(--prc-accent)]">{t.token_prefix}...</td>
                                            <td>{t.issued_by_email}</td>
                                            <td>{fmtYmd(t.created_at, String(t.created_at).substring(0, 10))}</td>
                                            <td>{t.last_used_at ? fmtYmdTime(t.last_used_at, String(t.last_used_at).substring(0, 16).replace("T", " ")) : <span className="text-[var(--prc-muted)]">未使用</span>}</td>
                                            <td className="text-right">
                                                <button onClick={() => revokeToken(t.id)} className="prc-btn-ghost text-xs text-[var(--prc-red)] inline-flex items-center gap-1" data-testid={`revoke-token-${t.id}`}>
                                                    <XCircle size={16} /> 失効
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="mt-3 text-xs text-[var(--prc-muted)] font-mono">
                        使用例: <code className="text-[var(--prc-accent)]">curl -H "Authorization: Bearer prc_xxxx" {window.location.origin}/api/vehicles</code>
                    </div>
                </section>
            )}

            {polling && (
                <div className="fixed bottom-6 right-6 prc-card p-4 flex items-center gap-3 z-50" data-testid="billing-polling">
                    <Clock size={24} className="text-[var(--prc-amber)] animate-pulse" />
                    <p className="text-sm">決済処理中...</p>
                </div>
            )}
        </div>
    );
}
